import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { searchOrders, getOrderDetails } from '../api';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SearchPanel from '../components/SearchPanel';
import StatusBadge from '../components/StatusBadge';
import Icon from '../components/Icon';
import AnimatedEmptyState from '../components/AnimatedEmptyState';
import '../css/orders.css';
import '../css/cards.css';
import '../css/pagination.css';
import '../css/list-summary.css';
import PageSizeDropdown from '../components/PageSizeDropdown';
import OrdersSortFiltersMenu from '../components/OrdersSortFiltersMenu';
import CustomSelect from '../components/CustomSelect';
import { pushDetailNavState } from '../utils/detailNavState';

const DEFAULT_ORDERS_PAGE_SIZE = 20;

const STATUS_DATE_FILTER_KEYS = ['inWorkFrom', 'inWorkTo', 'doneFrom', 'doneTo', 'canceledFrom', 'canceledTo'];
const EMPTY_STATUS_DATE_FILTERS = Object.fromEntries(STATUS_DATE_FILTER_KEYS.map((key) => [key, '']));

const escapeCsvCell = (val) => {
  const s = val == null ? '' : String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const Orders = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    totalPages: 0,
    totalElements: 0
  });
  const [searchParams, setSearchParams] = useState(() => {
    const savedSize = sessionStorage.getItem('ordersPageSize');
    return {
      title: '',
      status: '',
      createdFrom: '',
      createdTo: '',
      id: '',
      neverBought: null,
      ...EMPTY_STATUS_DATE_FILTERS,
      page: 0,
      size: savedSize ? parseInt(savedSize, 10) : DEFAULT_ORDERS_PAGE_SIZE,
      sort: 'updatedAt',
      direction: 'DESC'
    };
  });

  // Сохранение размера страницы в sessionStorage
  useEffect(() => {
    sessionStorage.setItem('ordersPageSize', searchParams.size.toString());
  }, [searchParams.size]);


  const [initialized, setInitialized] = useState(false);
  const STORAGE_KEY = 'ordersSearchParams';
  const statusLabels = t('orders.statusLabels', { returnObjects: true });

  const topPagerRef = React.useRef(null);
  const shouldScrollToPagerRef = React.useRef(false);

  // Restore filters on mount (priority: navigation state -> sessionStorage -> defaults)
  useEffect(() => {
    const stateParams = location.state?.searchParams;
    const storedParamsRaw = sessionStorage.getItem(STORAGE_KEY);

    if (stateParams) {
      const safeStateParams = { ...stateParams };
      delete safeStateParams.language;
      setSearchParams((prev) => ({ 
        ...prev, 
        ...safeStateParams,
        // Устанавливаем значения по умолчанию, если их нет
        sort: stateParams.sort || 'updatedAt',
        direction: stateParams.direction || 'DESC',
        size: stateParams.size || DEFAULT_ORDERS_PAGE_SIZE
      }));
    } else if (storedParamsRaw) {
      try {
        const parsed = JSON.parse(storedParamsRaw);
        const safeParsed = { ...parsed };
        delete safeParsed.language;
        setSearchParams((prev) => ({ 
          ...prev, 
          ...safeParsed,
          // Устанавливаем значения по умолчанию, если их нет
          sort: parsed.sort || 'updatedAt',
          direction: parsed.direction || 'DESC',
          size: parsed.size || DEFAULT_ORDERS_PAGE_SIZE
        }));
      } catch (e) {
        console.warn('Failed to parse stored search params', e);
      }
    }

    setInitialized(true);
  }, [location.state]);

  // Fetch orders based on search parameters
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await searchOrders({
        ...searchParams,
        id: searchParams.id?.trim()
      });
      setOrders(response.content || []);
      setPagination({
        totalPages: response.totalPages || 0,
        totalElements: response.totalElements || 0
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!initialized) return;
    fetchOrders();
  }, [fetchOrders, initialized]);

  // Persist filters for future visits
  useEffect(() => {
    if (!initialized) return;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(searchParams));
  }, [searchParams, initialized]);

  // Handle input changes for search form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams((prev) => {
      const newParams = { ...prev, [name]: value };
      // Сбрасываем страницу при изменении сортировки или размера страницы
      if (name === 'sort' || name === 'direction' || name === 'size') {
        newParams.page = 0;
      }
      return newParams;
    });
  };

  // Local state for debounced search input
  const [searchValue, setSearchValue] = useState(searchParams.title);

  // Sync internal search value with params (e.g. on reset)
  useEffect(() => {
    setSearchValue(searchParams.title);
  }, [searchParams.title]);

  // Debounce search input
  useEffect(() => {
    if (!initialized) return;
    const timer = setTimeout(() => {
      setSearchParams(prev => {
        if (prev.title === searchValue) return prev;
        return { ...prev, title: searchValue, page: 0 };
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue, initialized]);

  // Handle sort change from SortMenu
  const handleSortChange = (newSort) => {
    setSearchParams((prev) => ({ ...prev, sort: newSort, page: 0 }));
  };

  // Handle direction change from SortMenu
  const handleDirectionChange = (newDirection) => {
    setSearchParams((prev) => ({ ...prev, direction: newDirection, page: 0 }));
  };

  // Handle clear filters
  const clearFilters = () => {
    setSearchValue('');
    setSearchParams((prev) => ({
      title: '',
      status: '',
      createdFrom: '',
      createdTo: '',
      id: '',
      neverBought: null,
      ...EMPTY_STATUS_DATE_FILTERS,
      page: 0,
      size: prev.size,
      sort: 'updatedAt',
      direction: 'DESC',
    }));
  };

  // Handle neverBought toggle
  const handleNeverBoughtToggle = (checked) => {
    setSearchParams((prev) => ({
      ...prev,
      neverBought: checked ? true : null,
      page: 0
    }));
  };

  // Handle date changes from menu
  const handleDateFromChange = (value) => {
    setSearchParams((prev) => ({
      ...prev,
      createdFrom: value,
      page: 0
    }));
  };

  const handleDateToChange = (value) => {
    setSearchParams((prev) => ({
      ...prev,
      createdTo: value,
      page: 0
    }));
  };

  // Handle status transition dates (inWorkFrom, doneTo, ...)
  const handleStatusDateChange = (key, value) => {
    setSearchParams((prev) => ({
      ...prev,
      [key]: value,
      page: 0
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage, shouldScroll = false) => {
    if (shouldScroll) {
      shouldScrollToPagerRef.current = true;
    }
    setSearchParams((prev) => ({ ...prev, page: newPage }));
  };

  useEffect(() => {
    if (!shouldScrollToPagerRef.current) return;
    if (loading) return;

    const el = topPagerRef.current;
    if (!el) {
      const t = setTimeout(() => {
        const elRetry = topPagerRef.current;
        if (!shouldScrollToPagerRef.current) return;
        if (!elRetry) return;
        shouldScrollToPagerRef.current = false;
        elRetry.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
      return () => clearTimeout(t);
    }

    shouldScrollToPagerRef.current = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [searchParams.page, loading]);

  const getVisiblePages = () => {
    const totalPages = pagination.totalPages || 0;
    const current = searchParams.page;
    if (totalPages <= 1) return [];
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);

    const pages = [0];
    const left = Math.max(1, current - 1);
    const right = Math.min(totalPages - 2, current + 1);
    if (left > 1) pages.push('left-ellipsis');
    for (let p = left; p <= right; p += 1) pages.push(p);
    if (right < totalPages - 2) pages.push('right-ellipsis');
    pages.push(totalPages - 1);
    return pages;
  };

  const renderOrdersPager = (position = 'bottom') => {
    if ((loading && orders.length === 0) || pagination.totalPages <= 0) return null;
    const isFirst = searchParams.page <= 0;
    const isLast = searchParams.page >= pagination.totalPages - 1;
    const pages = getVisiblePages();

    return (
      <div 
        className={`orders-pager orders-pager-${position}`}
        ref={position === 'top' ? topPagerRef : null}
      >
        <div className="orders-pager-info">
          {t('common.pagination.showing', {
            from: pagination.totalElements > 0 ? searchParams.page * searchParams.size + 1 : 0,
            to: Math.min((searchParams.page + 1) * searchParams.size, pagination.totalElements),
            total: pagination.totalElements
          })}
        </div>

        <div className="orders-pager-controls">
          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => handlePageChange(searchParams.page - 1, position === 'bottom')}
            disabled={isFirst}
          >
            {t('common.pagination.previous')}
          </button>

          <div className="orders-pager-pages">
            {pages.map((item) => {
              if (typeof item !== 'number') {
                return (
                  <span key={item} className="orders-pager-ellipsis">
                    ...
                  </span>
                );
              }
              const active = item === searchParams.page;
              return (
                <button
                  key={item}
                  type="button"
                  className={`orders-pager-page ${active ? 'active' : ''}`}
                  onClick={() => handlePageChange(item, position === 'bottom')}
                  aria-current={active ? 'page' : undefined}
                >
                  {item + 1}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => handlePageChange(searchParams.page + 1, position === 'bottom')}
            disabled={isLast}
          >
            {t('common.pagination.next')}
          </button>

          <PageSizeDropdown
            value={searchParams.size}
            onChange={(newSize) => {
              shouldScrollToPagerRef.current = true;
              setSearchParams((prev) => ({ ...prev, size: parseInt(newSize, 10), page: 0 }));
            }}
            options={[10, 20, 50, 100, 200]}
            position={position}
          />
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${day}.${month}.${year}, ${hours}:${minutes}`;
    } catch {
      return 'Invalid Date';
    }
  };

  const statusOptions = [
    { value: '', label: t('orders.filters.allStatuses'), icon: 'layers' },
    { value: 'IN_PROGRESS', label: statusLabels['IN_PROGRESS'] || 'IN_PROGRESS', icon: 'clock' },
    { value: 'IN_WORK', label: statusLabels['IN_WORK'] || 'IN_WORK', icon: 'briefcase' },
    { value: 'DONE', label: statusLabels['DONE'] || 'DONE', icon: 'check-circle' },
    { value: 'DONE_DELETED', label: statusLabels['DONE_DELETED'] || 'DONE_DELETED', icon: 'trash-2' },
    { value: 'CANCELED', label: statusLabels['CANCELED'] || 'CANCELED', icon: 'x-circle' },
    { value: 'BLOCKED', label: statusLabels['BLOCKED'] || 'BLOCKED', icon: 'slash' }
  ];

  const filtersActive = useMemo(() => {
    const p = searchParams;
    return Boolean(
      (p.title && p.title.trim()) ||
        p.status ||
        p.createdFrom ||
        p.createdTo ||
        (p.id && String(p.id).trim()) ||
        p.neverBought === true ||
        STATUS_DATE_FILTER_KEYS.some((key) => p[key]) ||
        p.sort !== 'updatedAt' ||
        p.direction !== 'DESC'
    );
  }, [searchParams]);

  const statusFilter = (
    <CustomSelect
      value={searchParams.status || ''}
      options={statusOptions}
      onChange={(val) => handleInputChange({ target: { name: 'status', value: val } })}
      minWidth="180px"
      hasActiveFilter={Boolean(searchParams.status)}
    />
  );

  const downloadOrdersCsv = useCallback((list, filenamePrefix) => {
    const headers = ['userProfileId', 'fullName', 'defaultPhoneNumber'];
    const rows = list.map((o) => {
      const p = o.userProfileForOrderDto;
      return [p?.userProfileId ?? '', p?.fullName ?? '', p?.defaultPhoneNumber ?? ''].map(escapeCsvCell);
    });
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, []);

  const handleExportOrdersCsv = useCallback(() => {
    if (!orders.length) {
      window.alert(t('orders.csvExportEmpty'));
      return;
    }
    downloadOrdersCsv(orders, 'orders-customers-page');
  }, [orders, t, downloadOrdersCsv]);

  const [exportingAllOrders, setExportingAllOrders] = useState(false);

  const handleExportAllOrdersCsv = useCallback(async () => {
    if (exportingAllOrders) return;
    setExportingAllOrders(true);
    try {
      const response = await searchOrders({
        ...searchParams,
        id: searchParams.id?.trim(),
        page: 0,
        size: 100000
      });
      const all = response.content || [];
      if (!all.length) {
        window.alert(t('orders.csvExportEmpty'));
        return;
      }
      downloadOrdersCsv(all, 'orders-customers-all');
    } catch (err) {
      window.alert(err.message || 'Failed to export orders');
    } finally {
      setExportingAllOrders(false);
    }
  }, [exportingAllOrders, searchParams, t, downloadOrdersCsv]);

  const extraActions = (
    <OrdersSortFiltersMenu
      sort={searchParams.sort}
      direction={searchParams.direction}
      onSortChange={handleSortChange}
      onDirectionChange={handleDirectionChange}
      createdFrom={searchParams.createdFrom}
      createdTo={searchParams.createdTo}
      neverBought={searchParams.neverBought}
      onDateFromChange={handleDateFromChange}
      onDateToChange={handleDateToChange}
      onNeverBoughtToggle={handleNeverBoughtToggle}
      statusDates={Object.fromEntries(STATUS_DATE_FILTER_KEYS.map((key) => [key, searchParams[key]]))}
      onStatusDateChange={handleStatusDateChange}
      id={searchParams.id}
      onIdChange={(val) => setSearchParams((prev) => ({ ...prev, id: val, page: 0 }))}
      translationKey="orders"
    />
  );

  return (
    <div className="orders-page">
      <div className="orders-content-surface" style={{ marginBottom: '24px' }}>
        <div className="orders-controls-surface" style={{ marginBottom: 0 }}>
          <div className="list-summary-row">
            <div className="list-summary-header">
              <div className="list-summary-header-left">
                <div className="list-summary-icon">
                  <Icon name="package" size={24} />
                </div>
                <div>
                  <h3 className="list-summary-title">{t('orders.title')}</h3>
                  <p className="list-summary-subtitle">
                    {t('home.logs.subtitle', { count: pagination.totalElements })}
                  </p>
                </div>
              </div>
            </div>
            <div className="orders-csv-export-wrap">
              <button
                type="button"
                className="btn btn-secondary orders-csv-button"
                onClick={handleExportOrdersCsv}
                disabled={loading || orders.length === 0}
              >
                <Icon name="file-text" size={18} />
                {t('orders.exportCsv')}
              </button>
              <button
                type="button"
                className="btn btn-secondary orders-csv-button"
                onClick={handleExportAllOrdersCsv}
                disabled={exportingAllOrders}
              >
                <Icon name="file-text" size={18} />
                {exportingAllOrders ? t('orders.exporting') : t('orders.exportAll')}
              </button>
            </div>
          </div>

          <div className="search-panel-wrapper">
            <SearchPanel
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              onReset={clearFilters}
              loading={loading}
              placeholder={t('orders.filters.namePlaceholder')}
              statusFilter={statusFilter}
              extraActions={extraActions}
              filtersActive={filtersActive}
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Orders Cards */}
      <div className="orders-content-surface">
        <div className="orders-cards-wrapper">
        {renderOrdersPager('top')}
        {loading && orders.length === 0 ? (
          <div className="loading-state">
            <div className="strict-loader"></div>
            {t('orders.loading')}
          </div>
        ) : orders.length === 0 ? (
          <AnimatedEmptyState 
            iconName="package" 
            title={t('orders.empty.title')} 
            subtitle={t('orders.empty.subtitle')} 
          />
        ) : (
          <div className="cards-grid" style={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
            {orders.map((order) => (
              <Link
                key={order.id}
                to={`/orders/${order.id}`}
                state={pushDetailNavState(location, { fromOrders: { searchParams } })}
                className="item-card clickable-card"
              >
                <div className="card-header">
                  <h3 className="card-title" title={order.title}>
                    {order.title || t('common.notProvided')}
                  </h3>
                </div>

                <div className="card-body">
                  <div className="card-description">
                    {order.description || <span className="card-empty">—</span>}
                  </div>
                  
                  <div className="card-details-mini-grid">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '12px' }}>
                      <div className="card-detail-item" style={{ minWidth: 0, flex: 1 }}>
                        <Icon name="user" size={14} className="card-detail-icon" />
                        <span className="card-detail-text" title={order.userProfileForOrderDto?.fullName}>
                          {order.userProfileForOrderDto?.fullName || '—'}
                        </span>
                      </div>
                      <div className="card-detail-item" style={{ flexShrink: 0 }}>
                        <Icon name="package" size={14} className="card-detail-icon" />
                        <span className="card-detail-text">
                          {order.orderCompaniesCount != null ? (
                            `${order.orderCompaniesCount}/5 ${t('orders.purchased')}`
                          ) : '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-footer">
                  <StatusBadge type="order" status={order.status} />
                  <div className="card-date">
                    <Icon name="calendar" size={12} className="card-date-icon" />
                    <span>{formatDate(order.createdAt)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
        {renderOrdersPager('bottom')}
        </div>
      </div>
    </div>
  );
};

export default Orders;